import { useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import Button from '../ui/Button';
import type { SprayTncCurrent } from '../../services/sprayTnc';

interface Props {
  tnc: SprayTncCurrent;
  onAccept: () => Promise<boolean>;
}

/**
 * Blocking modal shown when the logged-in user has not yet accepted the
 * current spray module T&C. No dismiss path — the user either accepts or
 * leaves the page.
 */
function SprayTncModal({ tnc, onAccept }: Props) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAccept = async () => {
    if (!acknowledged) return;
    setSubmitting(true);
    setError(null);
    const ok = await onAccept();
    if (!ok) setError('Could not record acceptance. Please try again.');
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <header className="px-6 py-4 border-b border-neutral-200 flex items-center gap-3">
          <ShieldAlert className="h-5 w-5 text-primary-600" />
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">{tnc.title || 'Terms of Use'}</h2>
            {tnc.version && (
              <p className="text-xs text-neutral-500">Version {tnc.version}</p>
            )}
          </div>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/*
            Markdown is rendered as pre-wrapped plain text for now — we avoid
            pulling in a markdown library for one screen. The T&C content is
            admin-authored and vetted, so whitespace preservation is enough.
          */}
          <pre className="whitespace-pre-wrap font-sans text-sm text-neutral-700 leading-relaxed">
            {tnc.content || ''}
          </pre>
        </div>

        <footer className="px-6 py-4 border-t border-neutral-200 bg-neutral-50 rounded-b-xl">
          <label className="flex items-start gap-2 cursor-pointer mb-3">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={e => setAcknowledged(e.target.checked)}
              className="mt-0.5"
            />
            <span className="text-sm text-neutral-700">
              I have read and agree to the Spray Module terms of use above.
            </span>
          </label>
          {error && <p className="text-sm text-rose-600 mb-2">{error}</p>}
          <div className="flex justify-end">
            <Button
              onClick={handleAccept}
              disabled={!acknowledged || submitting}
              isLoading={submitting}
            >
              Accept and Continue
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default SprayTncModal;
